import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import logger from './utils/logger'
import { initializeTenant } from './services/tenantInit'
import './assets/styles/main.css'
import './assets/css/theme-variables.css'

// Log inizializzazione app
logger.info('MAIN', '🚀 Starting Pasticceria Gestionale App', {
    environment: import.meta.env.MODE,
    baseURL: import.meta.env.BASE_URL,
    apiURL: import.meta.env.VITE_API_URL,
    hostname: window.location.hostname
})

// Toast notifications
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'

// Toast options
const toastOptions = {
    position: 'top-right',
    timeout: 5000,
    closeOnClick: true,
    pauseOnFocusLoss: true,
    pauseOnHover: true,
    draggable: true,
    draggablePercent: 0.6,
    showCloseButtonOnHover: false,
    hideProgressBar: false,
    closeButton: 'button',
    icon: true,
    rtl: false,
    transition: 'Vue-Toastification__fade',
    maxToasts: 5,
    newestOnTop: true
}

// Initialize application with tenant context
const initializeApp = async () => {
    try {
        logger.info('MAIN', '🏢 Initializing tenant context...')

        // Initialize tenant BEFORE creating the app
        const tenantResult = await initializeTenant()

        if (tenantResult.success) {
            logger.info('MAIN', '✅ Tenant context initialized', {
                slug: tenantResult.tenant.slug,
                name: tenantResult.tenant.name,
                offline: tenantResult.offline || false
            })
        } else {
            logger.error('MAIN', '❌ Failed to initialize tenant context', {
                error: tenantResult.error
            })
            // Continue anyway - we'll use defaults
        }

        // Create Vue app
        const app = createApp(App)
        logger.info('MAIN', '🔧 Vue app created')

        // Create Pinia instance
        const pinia = createPinia()
        logger.info('MAIN', '📦 Pinia store initialized')

        // Use plugins
        app.use(pinia)
        logger.info('MAIN', '📱 Pinia plugin attached')

        app.use(router)
        logger.info('MAIN', '🗺️ Router attached')

        app.use(Toast, toastOptions)
        logger.info('MAIN', '🔔 Toast notifications enabled')

        // Global error handler con logging avanzato
        app.config.errorHandler = (err, instance, info) => {
            logger.error('VUE_ERROR', `Vue Application Error: ${err.message}`, {
                error: err.stack,
                component: instance?.$options.name || instance?.$options.__name || 'Unknown',
                info,
                props: instance?.$props,
                route: instance?.$route?.fullPath,
                timestamp: new Date().toISOString()
            })
            console.error('Global error:', err)
        }

        // Set tenant context in auth store if available
        if (tenantResult.success && tenantResult.tenant) {
            const { useAuthStore } = await import('./stores/auth')
            const authStore = useAuthStore()
            authStore.setTenantContext(tenantResult.tenant)
        }

        // Mount app
        logger.info('MAIN', '🏗️ Mounting Vue application to #app')
        app.mount('#app')
        logger.info('MAIN', '✅ Pasticceria Gestionale App successfully initialized and mounted')

        // Aggiungi logger alla finestra globale per debug
        window.__logger = logger
        logger.info('MAIN', '🔍 Logger available at window.__logger for debugging')

    } catch (error) {
        logger.error('MAIN', '❌ Fatal error during app initialization', {
            error: error.message,
            stack: error.stack
        })

        // Show error to user
        document.body.innerHTML = `
            <div style="display: flex; justify-content: center; align-items: center; height: 100vh; font-family: sans-serif;">
                <div style="text-align: center; max-width: 500px; padding: 2rem;">
                    <h1 style="color: #dc2626;">Errore di inizializzazione</h1>
                    <p>Si è verificato un errore durante l'avvio dell'applicazione.</p>
                    <p style="color: #6b7280; font-size: 0.875rem;">${error.message}</p>
                    <button onclick="window.location.reload()" style="margin-top: 1rem; padding: 0.5rem 1rem; background: #3b82f6; color: white; border: none; border-radius: 0.25rem; cursor: pointer;">
                        Riprova
                    </button>
                </div>
            </div>
        `
    }
}

// Start the application
initializeApp()
